/**
 * Integration Management Hooks
 * 
 * React hooks for managing external service integrations
 * including CRUD operations, authentication, and synchronization
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { 
  IntegrationConfig, 
  IntegrationType, 
  GMBIntegration,
  AnalyticsIntegration,
  WordPressIntegration,
  IntegrationHealth,
  IntegrationLog,
  GMBIntegrationFormData,
  AnalyticsIntegrationFormData,
  WordPressIntegrationFormData
} from '@/types/integrations'
import type { JobberIntegration, JobberIntegrationFormData } from '@/features/jobber/types'
import { useToast } from '@/hooks/use-toast'

// Integration Query Keys
const INTEGRATION_KEYS = {
  all: ['integrations'] as const,
  lists: () => [...INTEGRATION_KEYS.all, 'list'] as const,
  list: (filters: string) => [...INTEGRATION_KEYS.lists(), { filters }] as const,
  details: () => [...INTEGRATION_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...INTEGRATION_KEYS.details(), id] as const,
  health: (id: string) => [...INTEGRATION_KEYS.all, 'health', id] as const,
  logs: (id: string) => [...INTEGRATION_KEYS.all, 'logs', id] as const,
}

// Main Integrations Hook
export function useIntegrations(type?: IntegrationType) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch all integrations or filter by type
  const {
    data: integrations = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: INTEGRATION_KEYS.list(type || 'all'),
    queryFn: async () => {
      let query = supabase
        .from('integrations')
        .select('*')
        .order('created_at', { ascending: false })

      if (type) {
        query = query.eq('type', type)
      }

      const { data, error } = await query

      if (error) {
        throw new Error(`Failed to fetch integrations: ${error.message}`)
      }

      return data as IntegrationConfig[]
    },
  })

  // Create integration mutation
  const createIntegration = useMutation({
    mutationFn: async (integrationData: Partial<IntegrationConfig>) => {
      const { data, error } = await supabase
        .from('integrations')
        .insert([{
          ...integrationData,
          status: 'disconnected',
          error_count: 0,
          is_active: false,
        }])
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create integration: ${error.message}`)
      }

      return data as IntegrationConfig
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.lists() })
      toast({
        title: 'Integration Created',
        message: `${data.name} integration has been created successfully.`,
        type: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Creation Failed',
        message: error.message,
        type: 'error',
      })
    },
  })

  // Update integration mutation
  const updateIntegration = useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string
      updates: Partial<IntegrationConfig> 
    }) => {
      const { data, error } = await supabase
        .from('integrations')
        .update({ 
          ...updates, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update integration: ${error.message}`)
      }

      return data as IntegrationConfig
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.lists() })
      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.detail(data.id) })
      toast({
        title: 'Integration Updated',
        message: `${data.name} integration has been updated successfully.`,
        type: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Update Failed',
        message: error.message,
        type: 'error',
      })
    },
  })

  // Delete integration mutation
  const deleteIntegration = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('integrations')
        .delete()
        .eq('id', id)

      if (error) {
        throw new Error(`Failed to delete integration: ${error.message}`)
      }

      return id
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.lists() })
      toast({
        title: 'Integration Deleted',
        message: 'Integration has been deleted successfully.',
        type: 'success',
      })
    },
    onError: (error) => {
      toast({
        title: 'Deletion Failed',
        message: error.message,
        type: 'error',
      })
    },
  })

  return {
    integrations,
    isLoading,
    error,
    refetch,
    createIntegration: createIntegration.mutate,
    updateIntegration: updateIntegration.mutate,
    deleteIntegration: deleteIntegration.mutate,
    isCreating: createIntegration.isPending,
    isUpdating: updateIntegration.isPending,
    isDeleting: deleteIntegration.isPending,
  }
}

// Single Integration Hook
export function useIntegration(id: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const {
    data: integration,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: INTEGRATION_KEYS.detail(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integrations')
        .select('*')
        .eq('id', id)
        .single()

      if (error) {
        throw new Error(`Failed to fetch integration: ${error.message}`)
      }

      return data as IntegrationConfig
    },
    enabled: !!id,
  })

  // Test connection mutation
  const testConnection = useMutation({
    mutationFn: async () => {
      if (!integration) {
        throw new Error('Integration not found')
      }

      // This would call the appropriate API to test the connection
      // For now, simulate the test
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Update integration status based on test result
      const { data, error } = await supabase
        .from('integrations')
        .update({ 
          status: 'connected',
          last_sync: new Date().toISOString(),
          error_count: 0,
          last_error: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update connection status: ${error.message}`)
      }

      return data as IntegrationConfig
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.detail(id) })
      toast({
        title: 'Connection Successful',
        message: `${data.name} connection has been established.`,
        type: 'success',
      })
    },
    onError: (error) => {
      // Update integration with error status
      void supabase
        .from('integrations')
        .update({ 
          status: 'error',
          last_error: error.message,
          error_count: (integration?.error_count || 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.detail(id) })
      toast({
        title: 'Connection Failed',
        message: error.message,
        type: 'error',
      })
    },
  })

  // Manual sync mutation
  const syncIntegration = useMutation({
    mutationFn: async () => {
      if (!integration) {
        throw new Error('Integration not found')
      }

      // Update status to syncing
      await supabase
        .from('integrations')
        .update({ 
          status: 'syncing',
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.detail(id) })

      // Simulate sync process
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      // Update with completed sync
      const { data, error } = await supabase
        .from('integrations')
        .update({ 
          status: 'connected',
          last_sync: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to complete sync: ${error.message}`)
      }

      return data as IntegrationConfig
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.detail(id) })
      toast({
        title: 'Sync Completed',
        message: `${data.name} data has been synchronized successfully.`,
        type: 'success',
      })
    },
    onError: (error) => {
      // Update integration with error status
      void supabase
        .from('integrations')
        .update({ 
          status: 'error',
          last_error: error.message,
          error_count: (integration?.error_count || 0) + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)

      void queryClient.invalidateQueries({ queryKey: INTEGRATION_KEYS.detail(id) })
      toast({
        title: 'Sync Failed',
        message: error.message,
        type: 'error',
      })
    },
  })

  return {
    integration,
    isLoading,
    error,
    refetch,
    testConnection: testConnection.mutate,
    syncIntegration: syncIntegration.mutate,
    isTesting: testConnection.isPending,
    isSyncing: syncIntegration.isPending,
  }
}

// Integration Health Hook
export function useIntegrationHealth(id: string) {
  return useQuery({
    queryKey: INTEGRATION_KEYS.health(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_health')
        .select('*')
        .eq('integration_id', id)
        .single()

      if (error) {
        throw new Error(`Failed to fetch integration health: ${error.message}`)
      }

      return data as IntegrationHealth
    },
    enabled: !!id,
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

// Integration Logs Hook
export function useIntegrationLogs(id: string, limit = 50) {
  return useQuery({
    queryKey: INTEGRATION_KEYS.logs(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('integration_logs')
        .select('*')
        .eq('integration_id', id)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        throw new Error(`Failed to fetch integration logs: ${error.message}`)
      }

      return data as IntegrationLog[]
    },
    enabled: !!id,
  })
}

// Specialized Integration Hooks
export function useJobberIntegrations() {
  const { integrations, ...rest } = useIntegrations('jobber_crm')
  return {
    jobberIntegrations: integrations as JobberIntegration[],
    ...rest
  }
}

export function useGMBIntegrations() {
  const { integrations, ...rest } = useIntegrations('google_my_business')
  return {
    gmbIntegrations: integrations as GMBIntegration[],
    ...rest
  }
}

export function useAnalyticsIntegrations() {
  const gaIntegrations = useIntegrations('google_analytics')
  const gscIntegrations = useIntegrations('google_search_console')
  const fbIntegrations = useIntegrations('facebook_pixel')

  return {
    analyticsIntegrations: [
      ...(gaIntegrations.integrations || []),
      ...(gscIntegrations.integrations || []),
      ...(fbIntegrations.integrations || [])
    ] as AnalyticsIntegration[],
    isLoading: gaIntegrations.isLoading || gscIntegrations.isLoading || fbIntegrations.isLoading,
    error: gaIntegrations.error || gscIntegrations.error || fbIntegrations.error,
    refetch: async () => {
      await Promise.all([
        gaIntegrations.refetch(),
        gscIntegrations.refetch(),
        fbIntegrations.refetch(),
      ])
    }
  }
}

export function useWordPressIntegrations() {
  const { integrations, ...rest } = useIntegrations('wordpress')
  return {
    wordpressIntegrations: integrations as WordPressIntegration[],
    ...rest
  }
}

// Integration Form Helpers
export function useCreateJobberIntegration() {
  const { createIntegration } = useIntegrations()

  return useMutation({
    mutationFn: async (formData: JobberIntegrationFormData) => {
      const integrationData: Partial<JobberIntegration> = {
        name: formData.name,
        type: 'jobber_crm',
        credentials: {
          client_id: formData.client_id,
          client_secret: formData.client_secret,
        },
        settings: {
          sync_customers: formData.sync_customers,
          sync_jobs: formData.sync_jobs,
          sync_invoices: formData.sync_invoices,
          sync_estimates: formData.sync_estimates,
          auto_create_leads: formData.auto_create_leads,
          lead_status_mapping: {},
          franchise_region: formData.franchise_region,
        },
        sync_frequency: formData.sync_frequency,
        status: 'disconnected',
        error_count: 0,
        is_active: false,
      }

      createIntegration(integrationData)
    },
  })
}

export function useCreateGMBIntegration() {
  const { createIntegration } = useIntegrations()

  return useMutation({
    mutationFn: async (formData: GMBIntegrationFormData) => {
      const integrationData: Partial<GMBIntegration> = {
        name: formData.name,
        type: 'google_my_business',
        credentials: {
          account_id: formData.account_id,
        },
        settings: {
          auto_respond_to_reviews: formData.auto_respond_to_reviews,
          review_response_template: formData.review_response_template,
          post_approval_required: formData.post_approval_required,
          sync_business_hours: formData.sync_business_hours,
          monitor_performance: formData.monitor_performance,
          locations: formData.locations,
        },
        sync_frequency: formData.sync_frequency,
        status: 'disconnected',
        error_count: 0,
        is_active: false,
      }

      createIntegration(integrationData)
    },
  })
}

export function useCreateAnalyticsIntegration() {
  const { createIntegration } = useIntegrations()

  return useMutation({
    mutationFn: async (formData: AnalyticsIntegrationFormData) => {
      const integrationData: Partial<AnalyticsIntegration> = {
        name: formData.name,
        type: formData.integration_type,
        credentials: {
          property_id: formData.property_id,
          pixel_id: formData.pixel_id,
        },
        settings: {
          auto_import_data: formData.auto_import_data,
          track_conversions: formData.track_conversions,
          report_frequency: formData.report_frequency,
          custom_dimensions: [],
          goals: [],
        },
        sync_frequency: formData.sync_frequency,
        status: 'disconnected',
        error_count: 0,
        is_active: false,
      }

      createIntegration(integrationData)
    },
  })
}

export function useCreateWordPressIntegration() {
  const { createIntegration } = useIntegrations()

  return useMutation({
    mutationFn: async (formData: WordPressIntegrationFormData) => {
      const integrationData: Partial<WordPressIntegration> = {
        name: formData.name,
        type: 'wordpress',
        credentials: {
          site_url: formData.site_url,
          username: formData.username,
          password: formData.password,
          application_password: formData.application_password,
        },
        settings: {
          migrate_posts: formData.migrate_posts,
          migrate_pages: formData.migrate_pages,
          migrate_media: formData.migrate_media,
          migrate_comments: formData.migrate_comments,
          preserve_urls: formData.preserve_urls,
          update_internal_links: formData.update_internal_links,
          migration_batch_size: formData.migration_batch_size,
        },
        sync_frequency: 'manual',
        status: 'disconnected',
        error_count: 0,
        is_active: false,
      }

      createIntegration(integrationData)
    },
  })
}
